import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const t = {
    fr: {
      title: 'Mot de passe oublié',
      subtitle: 'Entrez votre email pour recevoir un lien de réinitialisation',
      email: 'Adresse email',
      emailPlaceholder: 'votre@email.com',
      submit: 'Envoyer le lien',
      sending: 'Envoi en cours...',
      backToLogin: 'Retour à la connexion',
      successTitle: 'Email envoyé !',
      successMessage: 'Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.',
      checkSpam: 'Pensez à vérifier vos spams.',
      tryAgain: 'Renvoyer l\'email'
    },
    en: {
      title: 'Forgot Password',
      subtitle: 'Enter your email to receive a reset link',
      email: 'Email address',
      emailPlaceholder: 'your@email.com',
      submit: 'Send reset link',
      sending: 'Sending...',
      backToLogin: 'Back to login',
      successTitle: 'Email sent!',
      successMessage: 'If an account exists with this email, you will receive a reset link.',
      checkSpam: 'Remember to check your spam folder.',
      tryAgain: 'Resend email'
    }
  };

  const text = t[language] || t.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSent(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'An error occurred');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5]/5 to-white p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">{text.successTitle}</CardTitle>
            <CardDescription className="mt-2">
              {text.successMessage}
              <br />
              <span className="text-xs">{text.checkSpam}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setSent(false)}
            >
              {text.tryAgain}
            </Button>
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {text.backToLogin}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5]/5 to-white p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#003DA5]/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-[#003DA5]" />
          </div>
          <CardTitle className="text-2xl">{text.title}</CardTitle>
          <CardDescription>{text.subtitle}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{text.email}</Label>
              <Input
                id="email"
                type="email"
                placeholder={text.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-password-email"
              />
            </div>

            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#003DA5] hover:bg-[#002d7a]"
              disabled={loading}
              data-testid="forgot-password-submit"
            >
              {loading ? text.sending : text.submit}
            </Button>

            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {text.backToLogin}
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
